import { KeyRound } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";

import { describeError } from "@/components/query-state";
import { Alert } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { getExecuteTrade } from "@/generated/api/arbitrages/arbitrages";
import { ApiError, SECRET_KEY_HEADER } from "@/lib/api/fetcher";
import { signIn } from "@/lib/auth/session";
import { useSession } from "@/lib/auth/use-session";

export function LoginScreen() {
    const { notice } = useSession();
    const [key, setKey] = useState("");
    const [error, setError] = useState<string | null>(null);
    const [checking, setChecking] = useState(false);

    // The key is accepted only once the bot has accepted it on a protected route.
    const submit = async (event: { preventDefault: () => void }) => {
        event.preventDefault();
        const candidate = key.trim();
        if (candidate.length === 0) return;
        setChecking(true);
        setError(null);
        try {
            await getExecuteTrade({ headers: { [SECRET_KEY_HEADER]: candidate } });
            signIn(candidate);
            toast.success("Logged in");
        } catch (err) {
            setError(err instanceof ApiError && err.status === 401 ? "Wrong key." : describeError(err));
        } finally {
            setChecking(false);
        }
    };

    return (
        <main className="flex min-h-screen items-center justify-center p-6">
            <Card className="w-full max-w-sm">
                <CardContent className="space-y-4 py-6">
                    <div className="space-y-1">
                        <h1 className="text-lg font-semibold">Arb bot</h1>
                        <p className="text-sm text-muted-foreground">Enter the API secret key.</p>
                    </div>
                    {notice && <Alert>{notice}</Alert>}
                    <form className="space-y-3" onSubmit={submit}>
                        <div className="space-y-2">
                            <Label htmlFor="secret-key">Password</Label>
                            <Input
                                id="secret-key"
                                type="password"
                                autoComplete="current-password"
                                autoFocus
                                value={key}
                                onChange={(event) => setKey(event.target.value)}
                            />
                        </div>
                        {error && (
                            <p role="alert" className="text-sm text-destructive">
                                {error}
                            </p>
                        )}
                        <Button type="submit" className="w-full" disabled={checking || key.trim().length === 0}>
                            <KeyRound />
                            {checking ? "Checking…" : "Log in"}
                        </Button>
                    </form>
                </CardContent>
            </Card>
        </main>
    );
}
